import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

type Running = { id: string; task_id: string; title: string; started_at: string } | null;
const fetchJson = async <T,>(path: string, init?: RequestInit) => {
  const r = await fetch(path, { credentials: 'include', ...init, ...(init?.body ? { headers: { 'content-type': 'application/json' } } : {}) });
  return (await r.json().catch(() => null)) as T;
};
export function fmt(seconds: number) {
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

/** The current person's running timer, shared by the bar, rows and panel. */
export function useTimer() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['ops-timer'], queryFn: () => fetchJson<Running>('/api/v1/ops/timer'), refetchInterval: 30_000 });
  const done = () => { qc.invalidateQueries({ queryKey: ['ops-timer'] }); qc.invalidateQueries({ queryKey: ['ops-list'] }); qc.invalidateQueries({ queryKey: ['ops-time'] }); };
  const start = useMutation({ mutationFn: (taskId: string) => fetchJson(`/api/v1/ops/tasks/${taskId}/timer`, { method: 'POST', body: '{}' }), onSuccess: done });
  const stop = useMutation({ mutationFn: () => fetchJson('/api/v1/ops/timer/stop', { method: 'POST', body: '{}' }), onSuccess: done });
  return { running: q.data ?? null, start: start.mutate, stop: () => stop.mutate() };
}

export function useTicker(since: string | undefined) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!since) return; const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, [since]);
  return since ? Math.max(0, Math.round((now - new Date(since).getTime()) / 1000)) : 0;
}

/**
 * Always-visible running bar (Manifest lesson: the timer had zero rows while it was hidden).
 * Warns after 4 hours, the classic forgotten timer.
 */
export function RunningBar() {
  const { running, stop } = useTimer();
  const secs = useTicker(running?.started_at);
  const navigate = useNavigate();
  if (!running) return null;
  const long = secs > 4 * 3600;
  return (
    <div className={`running-bar cx-rise${long ? ' running-bar--long' : ''}`} role="status" data-testid="running-bar">
      <span className="rec-dot" aria-hidden />
      <button type="button" className="cx-title-btn" onClick={() => navigate({ to: '/ops', search: { task: running.task_id } as never })}>{running.title}</button>
      <span className="cx-mono" data-testid="running-time">{fmt(secs)}</span>
      {long && <span className="cx-label">still running?</span>}
      <button type="button" className="cx-btn cx-btn--sm" onClick={stop} data-testid="running-stop">Stop</button>
    </div>
  );
}

export function TimerButton({ taskId }: { taskId: string }) {
  const { running, start, stop } = useTimer();
  const on = running?.task_id === taskId;
  return <button type="button" className={`timer-btn${on ? ' timer-btn--on' : ''}`} aria-label={on ? 'Stop timer' : 'Start timer'} title={on ? 'Stop timer' : 'Start timer'}
                 onClick={() => (on ? stop() : start(taskId))} data-testid="timer-btn">{on ? '■' : '▶'}</button>;
}
